import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailSubstructure,
  InsertHrailSubstructureBody,
  UpdateHrailSubstructureNewImageBody,
  UpdateHrailSubstructureRemoveImageBody,
} from "./types/substructures";

export const useHrailSubstructuresApi = () => {
  const api = useApi();

  const getAdminHrailSubstructure = useCallback(
    (substructureId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/substructures/${substructureId}`;
      return useSWRMutation<HrailSubstructure, CustomError, string>(url, (url) =>
        api.get<HrailSubstructure>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailSubstructures = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/substructures`, query);
      return useSWRImmutable<Paginated<HrailSubstructure>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailSubstructure>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailSubstructures = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/substructures`, query);
      return api.get<Paginated<HrailSubstructure>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailSubstructure = useCallback(
    async (body: InsertHrailSubstructureBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailSubstructureBody>(
        `/admin/settings/hrail/substructures`,
        body,
      );
      mutateCache([`/admin/settings/hrail/substructures`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailSubstructure = useCallback(
    async (
      substructureId: string,
      body: UpdateHrailSubstructureRemoveImageBody | UpdateHrailSubstructureNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/hrail/substructures/${substructureId}`;
      await api.put<UpdateHrailSubstructureRemoveImageBody | UpdateHrailSubstructureNewImageBody>(url, body);
      mutateCache([`/admin/settings/hrail/substructures`], url);
    },
    [api],
  );

  const updateAdminHrailSubstructureVisibility = useCallback(
    async (substructureId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/substructures/${substructureId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/substructures`]);
    },
    [api],
  );

  const updateAdminHrailSubstructureOrder = useCallback(
    async (substructureId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/substructures/${substructureId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/substructures`]);
    },
    [api],
  );

  const deleteAdminHrailSubstructure = useCallback(
    async (substructureId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/substructures/${substructureId}`);
      mutateCache([`/admin/settings/hrail/substructures`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (substructureId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/hrail/substructures/${substructureId}/image/generate_upload_url?file_name=${encodedFileName}`;
      const uploadUrl = await api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
      mutateCache([`/admin/settings/hrail/substructures`]);
      return uploadUrl;
    },
    [api],
  );

  return {
    getAdminHrailSubstructure,
    useAdminHrailSubstructures,
    getAdminHrailSubstructures,
    insertAdminHrailSubstructure,
    updateAdminHrailSubstructure,
    updateAdminHrailSubstructureVisibility,
    updateAdminHrailSubstructureOrder,
    deleteAdminHrailSubstructure,
    generateUploadImageUrl,
  };
};
